
import './App.css';
import {
  useHistory,
  Switch,
  Route,
  useLocation
} from "react-router-dom";

import { useEffect, useState } from 'react';
import { Hub, Auth } from 'aws-amplify';

import { GlobalActivityIndicator } from './shared/components/activityIndicator/ActivityIndicator';
import Header from './shared/components/header/Header';
import imageMainHeaderBackgroundBottom from './images/mainHeader_background_bottom_h66px.png';
import Core from './sections/core/Core';
import Marketplace from './sections/marketplace/Marketplace';
import Packs from './sections/packs/Packs';
import Pack from './shared/components/pack/Pack';
import Wallets from './sections/wallet/Wallets';
import { sortPets } from './shared/assetHelpers';

import {
  resetAll,
  setLoadComplete,
  setUser,
  setBraincloudSession,
  setForteData,
  setMagmicCoin,
  setUserTransactionPetIds,
  setUserTransactionsByPetId,
  setUserPets
 } from './redux/ducks/user';
import { useSelector, useDispatch } from 'react-redux';
import ReactGA from "react-ga4";


let signUpUsername; // used for detecting a first time sign in for a new user

function App() {

  const mainHeaderBackgroundBottomHeight = 50;
  const [scrollPosition, setScrollPosition] = useState(0);
  useEffect( // watch for scroll position changes
    () => {
      const handleScroll = () => {
        const position = window.pageYOffset;
        setScrollPosition(position);
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();
      return () => {
        window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const dispatch = useDispatch();

  const location = useLocation();
  const locationPathname = location.pathname;

  const userReduxStore = useSelector(state => {
    return state.user;
  });

  useEffect(() => {
    let googleAnalyticsId = undefined;
    switch (process.env.REACT_APP_ENV) {
      case "staging":    
        googleAnalyticsId = "G-8T38RRW0P0";
        break;
      default:
        googleAnalyticsId = "G-M41K16R9DV";
        break;
    }

    ReactGA.initialize(googleAnalyticsId);

  }, []);

  useEffect(() => {
    let reactGAPage = locationPathname;
    // for exact matching on the pathname, omit a trailing forward-slash if present
    if (reactGAPage.length > 1) {
      if (reactGAPage.endsWith("/")) {
        reactGAPage = reactGAPage.slice(0, -1);
      }
    }
    let reactGAPageTitle;
    switch (reactGAPage) {
      case "/":
        reactGAPageTitle = "Home";
        break;
      case "/nfts":
        reactGAPageTitle = "NFTs";
        break;
      case "/marketplace":
        reactGAPageTitle = "Marketplace";
        break;
      case "/wallet":
        reactGAPageTitle = "Wallet";
        break;
      default: // only tracking main pages
        return; // therefore, halt on any non-exact matches
    }
    const sendData = {
      hitType: "pageview",
      page: reactGAPage,
      title: reactGAPageTitle
    };
    if (!window.location.href.startsWith("http://localhost")) {
      ReactGA.send(sendData);
    } else {
      console.log("Google Analytics pageviews omitted for localhost env", sendData);
    }
  }, [locationPathname]);

  // provide a secondary class for Wallets.css to alter the main layout elements during sign in/up
  let mainSectionClassName = "mainSection";
  let mainFooterClassName = "mainFooter";
  if (locationPathname.startsWith("/wallet") && !userReduxStore.user.username) {
    mainSectionClassName += " signingIn";
    mainFooterClassName += " signingIn";
  }

  const history = useHistory();
  useEffect( // managing user session data
    () => {
      let updateUser = async (data) => {

        if (data && data.payload) {
          switch (data.payload.event) { // https://docs.amplify.aws/guides/authentication/listening-for-auth-events/q/platform/js/
            case 'signIn':
              if (!signUpUsername) {
                if (!window.location.href.startsWith("http://localhost")) {
                  ReactGA.event("login"); // https://developers.google.com/tag-platform/gtagjs/reference/events#login
                }
              } else {
                if (!window.location.href.startsWith("http://localhost")) {
                  ReactGA.event("sign_up"); // https://developers.google.com/tag-platform/gtagjs/reference/events#sign_up
                }
                const signInUsername = data.payload.data && data.payload.data.attributes? data.payload.data.attributes.email : undefined;
                if (signUpUsername === signInUsername) {
                  signUpUsername = undefined;
                  if (!locationPathname.startsWith("/wallet")) {
                    window.scrollTo(0, 0); // scroll-to-top
                    history.push("/wallet");
                    return;
                  }
                }
              }
            case 'signOut':
              break;
            case 'configured':
            case 'signUp':
              signUpUsername = data.payload.data && data.payload.data.user? data.payload.data.user.username : undefined;
            case 'signIn_failure':
            case 'signUp_failure':
              return; // message/error will be displayed in Amplify UI component; therefore, do nothing here.
            default:
              console.log("Unknown user state event", data.payload.event);
          }
        }

        signUpUsername = undefined; // reset the first time sign in flag

        dispatch(resetAll());
        try {
          let user = await Auth.currentAuthenticatedUser(); // get the authenticated user from AWS Auth

          let bcConnectedUserId = user.attributes && user.attributes.hasOwnProperty("custom:bc_user_id") ? user.attributes["custom:bc_user_id"] : undefined;
          let bcConnectedUserPassword = user.attributes && user.attributes.hasOwnProperty("custom:bc_password") ? user.attributes["custom:bc_password"] : undefined;

          user.bcConnectedUserId = bcConnectedUserId;
          user.bcConnectedUserPassword = bcConnectedUserPassword;

          let apiEndpointURL = "";
          switch (process.env.REACT_APP_ENV) {
            case "staging":
              apiEndpointURL = "https://cf35m63tng.execute-api.us-east-1.amazonaws.com/test/user/login";
              break;
            case "prod":
              apiEndpointURL = "https://q645yqsbml.execute-api.us-east-1.amazonaws.com/prod/user/login";
              break;
            default:
              console.warn("Using production endpoints by default.");
              apiEndpointURL = "https://q645yqsbml.execute-api.us-east-1.amazonaws.com/prod/user/login";
              break;
          }
          dispatch(setUser(user));

          if (bcConnectedUserId && bcConnectedUserPassword) {
            const requestOptions = {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                userId: bcConnectedUserId,
                password: bcConnectedUserPassword
              })
            };
            fetch(apiEndpointURL, requestOptions)
              .then(response => response.json())
              .then(data => {

                const braincloudSession = {
                  sessionId: data.response && data.response.hasOwnProperty("sessionId") ? data.response.sessionId : undefined,
                  bcId: data.response && data.response.hasOwnProperty("bcId") ? data.response.bcId : undefined
                }
                dispatch(setBraincloudSession(braincloudSession));

                const forteData = data.response && data.response.hasOwnProperty("forteData") ? data.response.forteData : {};
                dispatch(setForteData(forteData));

                let magmicCoin = data.response && data.response.hasOwnProperty("magmicCoin") ? data.response.magmicCoin : undefined;
                if (!isNaN(magmicCoin)) {
                  magmicCoin = parseInt(magmicCoin);
                } else {
                  magmicCoin = undefined;
                }
                dispatch(setMagmicCoin(magmicCoin));

                const transactions = data.response && data.response.transactions ? data.response.transactions : [];
                let transactionPetIds = [];
                let transactionsByPetId = {};
                transactions.forEach((transaction) => {
                  const petId = transaction.pet ? transaction.pet.id : undefined;
                  if (petId) {
                    transactionPetIds.push(petId);
                    transactionsByPetId[petId] = transaction;
                  }
                });
                dispatch(setUserTransactionPetIds(transactionPetIds));
                dispatch(setUserTransactionsByPetId(transactionsByPetId));
                
                const pets = data.response && data.response.pets ? data.response.pets : [];

                sortPets(pets);

                dispatch(setUserPets(pets));

                dispatch(setLoadComplete());
              });
            } else {
              dispatch(setLoadComplete());
            }
        } catch {
          dispatch(setUser({}));
          dispatch(setForteData({}));
          dispatch(setBraincloudSession({}));
          dispatch(setMagmicCoin(undefined));
          dispatch(setUserTransactionPetIds([]));
          dispatch(setUserTransactionsByPetId({}));
          dispatch(setUserPets([]));
          dispatch(setLoadComplete());
        }
      }
      Hub.listen('auth', updateUser); // listen for login/signup events from AWS Hub
      updateUser(); // check manually the first time because we won't get a Hub event
      return () => Hub.remove('auth', updateUser) // cleanup (overkill for App.js but included for completeness)
    },
    // In addition to being subscribed to subsequent AWS Hub 'auth' events,
    // listen for whenever components request data refresh
    [userReduxStore.dataRefreshRequestedDate]
  );

  let mainHeaderBackgroundBottomClassName = "mainHeaderBackgroundBottom";
  if (scrollPosition > mainHeaderBackgroundBottomHeight) {
    mainHeaderBackgroundBottomClassName += " shrinkForScroll";
  }

  return (
    <>
      <GlobalActivityIndicator />
      <Header />
      <section className={mainSectionClassName}>
        <div className={mainHeaderBackgroundBottomClassName}>
          <img src={imageMainHeaderBackgroundBottom} />
        </div>
        {/* A <Switch> looks through its children <Route>s and
            renders the first one that matches the current URL. */}
        <Switch>
          <Route path="/store">
            <Marketplace />
          </Route>
          <Route path="/packs">
            <Packs />
          </Route>
          <Route path="/nfts">
            <Packs />
          </Route>
          <Route path="/pack/:packID">
            <Pack />
          </Route>
          <Route path="/marketplace">
            <Marketplace />
          </Route>
          <Route path="/wallet/:forwardedAction">
            <Wallets />
          </Route>
          <Route path="/wallet">
            <Wallets />
          </Route>
          <Route path="/">
            <Core />
          </Route>
        </Switch>
      </section>
      <footer className={mainFooterClassName}>
        {/*
        <div className="additionalLinks">
          <div className="additionalLinksContentWrapper">
            <div>
              <h2>
                ADDITIONAL LINKS ETC
              </h2>
            </div>
            <div>
              <p>
                This is the Footer "Widget Area". Anything can go here really.
              </p>
            </div>
            <div>
              <ul>
                <li><a href="https://magmic.com" target="_blank">Magmic Inc.</a></li>
                <li><a href="https://magmic.com/terms-of-service/" target="_blank">Terms of Service</a></li>
                <li><a href="https://magmic.com/privacy-policy" target="_blank">Privacy Policy</a></li>
              </ul>
            </div>
            <div>
              <ul>
                <li><Link to="/">Link #4</Link></li>
                <li><Link to="/">Link #5</Link></li>
                <li><Link to="/">Link #6</Link></li>
              </ul>
            </div>
          </div>
        </div>*/}
        <div className="legal">
          <div className="legalContentWrapper">
            Copyright © <a href="https://magmic.com" target="_blank">MAGMIC Inc.</a> All Rights Reserved. : <a href="https://magmic.com/terms-of-service/" target="_blank">Terms of Service</a> | <a href="https://magmic.com/privacy-policy" target="_blank">Privacy Policy</a>
          </div>
        </div>
      </footer>
    </>
  );
}

export default App;